import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 * @property {HTMLButtonElement} [toggleButton] - The pause and play control.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    // WCAG 2.3.3 Animation from Interactions: background videos autoplay
    // at load which is a potential vestibular trigger. When reduced motion
    // is active, skip loading the sources entirely. The <video> element's
    // poster image remains visible, preserving the visual design.
    if (this.#shouldReduceMotion()) {
      videoElement.removeAttribute('autoplay');
      videoElement.pause();
      this.refs.toggleButton?.setAttribute('hidden', '');
      return;
    }

    const signal = this.#abortController.signal;
    videoElement.addEventListener('play', () => this.#syncToggle(false), { signal });
    videoElement.addEventListener('pause', () => this.#syncToggle(true), { signal });

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }

  togglePlayback() {
    const { videoElement } = this.refs;
    if (!videoElement) return;

    if (videoElement.paused) {
      this.#syncToggle(false);
      videoElement.play()?.catch(() => this.#syncToggle(true));
    } else {
      videoElement.pause();
      this.#syncToggle(true);
    }
  }

  /**
   * @param {boolean} paused
   */
  #syncToggle(paused) {
    const { toggleButton } = this.refs;
    if (!toggleButton) return;

    toggleButton.setAttribute('aria-pressed', String(paused));
    toggleButton.querySelector('[data-icon="pause"]')?.toggleAttribute('hidden', paused);
    toggleButton.querySelector('[data-icon="play"]')?.toggleAttribute('hidden', !paused);
  }

  #shouldReduceMotion() {
    const body = document.body;
    if (body && body.classList.contains('nh-reduced-motion')) return true;
    const respectAttr = body && body.getAttribute('data-respect-reduced-motion');
    const respects = respectAttr !== 'false';
    return respects && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
